import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { type FC, useState } from 'react';
import { Card, Modal } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@furality/furality-purpose';
import { CardLayout } from '@/components/layouts/index.tsx';

type RedirectInfoProps = { redirectLink: string };
const RedirectInfo: FC<RedirectInfoProps> = ({ redirectLink }) => (
  <span className="text-info">{redirectLink}</span>
);

const ExternalRedirect = () => {
  const { t } = useTranslation(['common', 'link']);
  const { a: isMatureContent, r: redirectLink } = useSearch({ from: '/link' });
  const [showAdultConfirm, setShowAdultConfirm] = useState(false);

  /**
   * Concatenate `https://` to the beginning of the link if there's no protocol in the url.
   * Just to be resilient, to prevent the href opening as a relative link accidentally.
   */
  const redirectLinkWithPrototcol = redirectLink.match(/^[a-zA-Z]+:\/\//)
    ? redirectLink
    : `https://${redirectLink}`;

  return (
    <Card.Body>
      <div className="text-center">
        <img
          // CON-SPECIFIC: event-specific logo
          src="https://cdn.furality.org/files/web/logo/furality-flower-festival-logo-md.webp"
          alt=""
          className="img-fluid"
        />
      </div>
      <h4 className="text-center mt-5">{t('link:leaving_fox_portal')}</h4>
      <p className="text-center">
        <Trans
          i18nKey="press_continue_to_be_redirected_to"
          components={{
            redirectLinkText: <RedirectInfo redirectLink={redirectLink} />,
          }}
        />
      </p>
      <div className="d-flex flex-column flex-sm-row justify-content-between">
        {/* These buttons stack and become full width when mobile sized */}
        <Button variant="secondary" className="m-1" size="lg" onClick={() => window.close()}>
          {t('shared.go_back')}
        </Button>
        {isMatureContent ? (
          <Button
            variant="warning"
            className="m-1"
            size="lg"
            onClick={() => setShowAdultConfirm(true)}
          >
            {t('shared.continue')}
          </Button>
        ) : (
          <Button
            as="a"
            variant="primary"
            className="m-1"
            size="lg"
            href={redirectLinkWithPrototcol}
          >
            {t('shared.continue')}
          </Button>
        )}
      </div>

      <Modal
        show={showAdultConfirm}
        onHide={() => setShowAdultConfirm(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('link:adult_content_modal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Trans
            ns="link"
            i18nKey="adult_content_modal.body"
            components={{
              strong: <strong />,
              p: <p />,
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdultConfirm(false)}>
            {t('shared.cancel')}
          </Button>
          <Button as="a" variant="warning" href={redirectLinkWithPrototcol}>
            {t('shared.continue')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card.Body>
  );
};

const LinkSearch = Type.Object({
  // adult
  a: Type.Boolean({ default: false }),
  // link
  r: Type.String(),
});
export const Route = createFileRoute('/link')({
  component: () => (
    <CardLayout title="shared.navigation.link">
      <ExternalRedirect />
    </CardLayout>
  ),
  validateSearch: (search) => Value.Cast(LinkSearch, search),
});
